import React, { Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import RenderLimitedText from "go-core/components/RenderLimitedText";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import ClientGrantStatusLabel, { getClientGrantStatusLabelTitle } from "../../components/ClientGrantStatusLabel";
import { ClientDetailsImportContext, ClientGrantApi } from "../../services/types";

interface ListState {
	requests: ClientDetailsImportContext;
	resource?: Record<string, any>;
}

interface Props {
	requests: ClientDetailsImportContext;
}

const listName = "GRANT";
const resourceType = "GRANT";

const List = ({ resource, requests }: ListState) => {
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	let config: ListConfig = {
		fields: [
			{
				id: "client_id",
				name: t("lib:go_component.client_details.field.client_id.title"),
			},
			{
				id: "organization_name",
				name: t("lib:go_component.client_details.field.organization_name.title"),
			},
			{
				id: "granted_scopes",
				name: "Scopes",
				render: (item: ClientGrantApi) => (
					<RenderLimitedText minWidth={200}>{item.granted_scopes}</RenderLimitedText>
				),
			},
			{
				id: "status",
				name: t("lib:common.word.status"),
				render: (item: ClientGrantApi) => <ClientGrantStatusLabel status={item.status} />,
				renderExport: (item: ClientGrantApi) => getClientGrantStatusLabelTitle(t, item.status),
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				render: (item: ClientGrantApi) => <>{FormatDate(item.created_at)}</>,
			},
			{
				id: "revoked_at",
				name: t("lib:go_component.client_details.field.revoked_at.title"),
				render: (item: ClientGrantApi) => <>{FormatDate(item.revoked_at ?? "")}</>,
			},
		],
		selectedColumns: ["client_id", "organization_name", "granted_scopes", "status", "created_at", "revoked_at"],
		segments: [
			{
				id: "all",
				name: t("lib:common.word.all"),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			return requests.getAllClientGrants(params, { cancelToken: sourceToken?.token });
		},
		withoutExportActions: true,
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		numberOfStickyColumnsAtTheStart: 1,
	};
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};

	return (
		<Suspense fallback={<LoadingContainer />}>
			<ListData config={config} />
		</Suspense>
	);
};

const ClientDetailsGrantsPage = ({ requests }: Props) => {
	const isMobile = useWindowSize().isMobile;
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle("Client grants");
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
	}, []);

	return (
		<>
			{!isMobile && <Header title="Client grants" />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} requests={requests} />
			</Suspense>
		</>
	);
};

export default ClientDetailsGrantsPage;
